import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Button, Input, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useDeleteConfigurationFileMutation } from "../../../redux/configurationFiles/configurationFilesAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { updateOpenModalDeleteConfigurationFile } from "../../../redux/configurationFiles/configurationFilesSlice";
// UTILS
import { styleDeleteButton } from "../../../utils/utils";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";

function ConfigurationFilesDeleteModal() {
  // ************************************************ */
  // LOCAL STORAGE AND VARIABLES ******************** */
  const dispatch = useDispatch();
  const { configurationFile, openModalDeleteConfigurationFile } = useSelector(
    (state: RootState) => state.configurationFile,
  );
  const [disableButtonConfirmDelete, setDisableButtonConfirmDelete] = useState(true);
  const [confirmName, setConfirmName] = useState("");
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerDeleteConfigurationFile,
    { isLoading, isSuccess, error, isError, reset: resetDeleteConfigurationFile },
  ] = useDeleteConfigurationFileMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickClose = () => {
    dispatch(updateOpenModalDeleteConfigurationFile(false));
  };

  const handleDeleteItem = async () => {
    const token = await GETJwtToken();
    triggerDeleteConfigurationFile({ id: configurationFile.id, token });
    onClickClose();
  };

  const handleSubmitDeleteItem = async (event: any) => {
    if (event.key === "Enter" && !disableButtonConfirmDelete) {
      const token = await GETJwtToken();
      triggerDeleteConfigurationFile({ id: configurationFile.id, token });
      onClickClose();
    }
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (openModalDeleteConfigurationFile) {
      setDisableButtonConfirmDelete(true);
      setConfirmName("");
      resetDeleteConfigurationFile();
    }
  }, [openModalDeleteConfigurationFile]);

  useEffect(() => {
    setDisableButtonConfirmDelete(true);
    if (
      confirmName &&
      configurationFile &&
      configurationFile.name &&
      confirmName.toLowerCase() === configurationFile.name.toLowerCase()
    ) {
      setDisableButtonConfirmDelete(false);
    } else {
      setDisableButtonConfirmDelete(true);
    }
  }, [confirmName]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Modal
      closable={false}
      onCancel={onClickClose}
      open={openModalDeleteConfigurationFile}
      footer={null}
    >
      <Spin spinning={isLoading}>
        <div>
          <div className="text-center">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="generalStyles__warning generalStyles__deleteItemIcon"
            />
            <p className="mt-3 drawer__text">
              {t("organizationManagement.deleteDescription.text1")}
              <b className="generalStyles__warning">
                {t("organizationManagement.deleteDescription.text2")}{" "}
              </b>
              {t("configuration.configurationFiles.theConfigurationFile")}{" "}
              <b className="generalStyles__warning">
                {configurationFile && configurationFile.name ? configurationFile.name : ""}{" "}
              </b>
              {t("configuration.configurationFiles.confirmConfigurationFileName")}{" "}
            </p>
            <Input
              placeholder={t("configuration.configurationFiles.configurationFileName")}
              onChange={(e) => {
                setConfirmName(e.target.value);
              }}
              value={confirmName}
              onKeyDown={handleSubmitDeleteItem}
              status="warning"
            />
          </div>
          <div className="generalStyles__flexEnd mt-4">
            <div>
              <Button
                htmlType="submit"
                className={styleDeleteButton(disableButtonConfirmDelete, theme)}
                onClick={handleDeleteItem}
                disabled={disableButtonConfirmDelete}
              >
                {t("organizationManagement.delete")}
              </Button>
              <Button onClick={onClickClose} className="buttonStyle__4">
                {t("organizationManagement.close")}
              </Button>
            </div>
          </div>
        </div>
      </Spin>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isError}
        isSuccess={isSuccess}
        requestType="DELETE"
        error={error}
        name="Adapter"
      />
    </Modal>
  );
}

export default ConfigurationFilesDeleteModal;
